<script setup lang="ts">
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxRoot,
  ComboboxViewport,
  TagsInputInput,
  TagsInputItem,
  TagsInputItemDelete,
  TagsInputItemText,
  TagsInputRoot,
} from "reka-ui";
import debounce from "lodash.debounce";
import { computed, ref, watch } from "vue";

import { caseService } from "@/services";

import { useBrandStore } from "@/stores/brand-store";

import { getFieldNames } from "./getNames";
import CaseSearchFieldDescription from "./CaseSearchFieldDescription.vue";

import type { AutocompleteFilterField } from "@/models/Column";

withDefaults(defineProps<{
  config: AutocompleteFilterField;
  fieldPath: string;
  displayName: string;
  isCollection: boolean;
  lastSearch?: { value?: string[] };
}>(), {  })

const isMultiple = computed(() => __props.config.multiple !== false);
const values = ref<string | string[]>(isMultiple.value ? (__props.lastSearch?.value ?? []) : (__props.lastSearch?.value?.[0] ?? ""));

const open = ref(false);
const isLoading = ref(false);

const searchText = ref("");
const matchedWords = ref<string[]>([]);

const selectedValues = computed(() => (Array.isArray(values.value) ? values.value : [values.value]));
const notSelectedWords = computed(() => matchedWords.value.filter((word) => !selectedValues.value.includes(word)));

const searchTextAtUpdate = ref("");
const { fieldTypeName, fieldValueName, fieldIsCollectionName, fieldHelpTextId } = getFieldNames(__props.fieldPath, __props.config);

watch(
  searchText,
  debounce(async (text: string) => {
    const { selectedClient, selectedBrand } = useBrandStore();
    if (selectedClient == null || selectedBrand == null) return;

    text = text.trim();
    searchTextAtUpdate.value = text;

    if (text === "") {
      matchedWords.value = [];
      open.value = false;
      return;
    }

    isLoading.value = true;
    const result = await caseService.CaseSearchAutocomplete(selectedClient, selectedBrand, {
      searchText: text,
      searchFields: [__props.fieldPath],
    });

    // Verify the latest search text is the result being applied
    if (searchTextAtUpdate.value === text) {
      matchedWords.value = result;
      isLoading.value = false;
    }
  }, 100),
);

watch(
  values,
  () => {
    searchText.value = "";
    open.value = false;
  },
  { deep: true },
);

function onBlur(event: FocusEvent) {
  const { currentTarget, relatedTarget } = event;

  if (!(currentTarget as HTMLElement).contains(relatedTarget as HTMLElement)) {
    searchText.value = "";
    matchedWords.value = [];
  }
}

function onDelete() {
  matchedWords.value = [];
}
</script>
<template>
  <div class="max-w-md tw-relative tw-shrink-0 tw-space-y-1">
    <label
      tabindex="0"
      :for="fieldValueName"
      class="tw-block tw-text-sm tw-font-medium tw-underline-offset-2 tw-outline-none focus:tw-underline"
    >
      {{ displayName }}
    </label>
    <CaseSearchFieldDescription v-if="config.helpText" :id="fieldHelpTextId" :help-text="config.helpText" />
    <input type="hidden" :name="fieldTypeName" value="autocomplete" />
    <input type="hidden" :name="fieldIsCollectionName" :value="isCollection" />
    <template v-if="values.length > 0">
      <input
        v-for="(value, idx) in Array.isArray(values) ? values : [values]"
        :key="idx"
        type="hidden"
        :value="value"
        :name="`${fieldValueName}[${idx}]`"
      />
    </template>
    <ComboboxRoot
      v-model="values"
      v-model:open="open"
      :multiple="isMultiple"
      class="tw-relative tw-flex tw-w-full"
      @blur="onBlur"
    >
      <ComboboxAnchor
        class="tw-w-full tw-gap-1 tw-rounded-md tw-border tw-border-gray-500 tw-bg-white tw-p-2 tw-text-sm tw-leading-none tw-outline-none"
      >
        <TagsInputRoot
          v-if="isMultiple"
          v-slot="{ modelValue: tags }"
          delimiter=""
          class="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-md"
          :model-value="Array.isArray(values) ? values : [values]"
          @update:model-value="(ar) => (values = ar)"
        >
          <TagsInputItem
            v-for="item in tags as string[]"
            :key="item"
            tabindex="0"
            :value="item"
            class="tw-flex tw-items-center tw-justify-between tw-gap-1 tw-rounded tw-bg-gray-800 tw-px-2 tw-py-1 tw-text-white"
            @vue:unmounted="onDelete"
          >
            <TagsInputItemText class="tw-text-sm" />
            <TagsInputItemDelete
              tabindex="0"
              aria-label="Delete"
              class="tw-grid tw-h-6 tw-w-6 tw-place-items-center tw-rounded-full tw-bg-[inherit] tw-text-white hover:tw-brightness-75"
              :aria-labelledby="undefined"
            >
              <i class="fa fa-x fa-xs" aria-hidden="true"></i>
            </TagsInputItemDelete>
          </TagsInputItem>
          <ComboboxInput v-model="searchText" as-child>
            <TagsInputInput
              :id="fieldValueName"
              placeholder="Type here..."
              :aria-describedby="config.helpText ? fieldHelpTextId : undefined"
              class="tw-h-6 tw-flex-1 tw-rounded tw-bg-transparent tw-px-1 focus:tw-outline-none"
              @keydown.enter.prevent
            />
          </ComboboxInput>
          <div v-if="isLoading" class="tw-grid tw-place-items-center tw-py-2">
            <i class="fas fa-spinner fa-spin fa-sm"></i>
          </div>
        </TagsInputRoot>
        <ComboboxInput
          v-else
          v-model="searchText"
          :id="fieldValueName"
          placeholder="Type here..."
          :aria-describedby="config.helpText ? fieldHelpTextId : undefined"
          class="tw-h-6 tw-flex-1 tw-rounded tw-bg-transparent tw-px-1 focus:tw-outline-none"
          @keydown.enter.prevent
        />
      </ComboboxAnchor>
      <ComboboxContent
        position="popper"
        :avoid-collisions="true"
        :side-offset="4"
        :collision-padding="{ bottom: 10 }"
        class="tw-z-10 tw-w-[--reka-combobox-trigger-width] tw-overflow-hidden tw-rounded tw-border tw-border-gray-500 tw-bg-white tw-shadow-md"
      >
        <ComboboxViewport class="tw-p-1">
          <ComboboxEmpty class="tw-py-2 tw-text-center tw-text-xs tw-font-medium tw-text-gray-400">
            {{ isLoading ? "Loading" : "No options" }}
          </ComboboxEmpty>
          <ComboboxGroup>
            <ComboboxItem
              v-for="(word, index) in notSelectedWords"
              :key="`${word}-${index}`"
              class="tw-flex tw-min-h-7 tw-select-none tw-items-center tw-rounded-sm tw-pl-5 hover:tw-cursor-pointer data-[highlighted]:tw-bg-gray-200 data-[highlighted]:tw-text-gray-800 data-[highlighted]:tw-outline-none"
              :value="word"
            >
              <span class="tw-line-clamp-2 tw-text-sm">
                {{ word }}
              </span>
            </ComboboxItem>
          </ComboboxGroup>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxRoot>
  </div>
</template>
